/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";

function normalizeAdminRole(value) {
  const raw = String(value || "").trim().toLowerCase();
  return raw === "manager" ? "manager" : "admin";
}

function isAdminLike(role) {
  const value = String(role || "").trim().toLowerCase();
  return value === "admin" || value === "manager";
}

function displayStatus(item) {
  if (!item) return "inactive";
  if (item.revokedAt) return "revoked";
  if (item.deletedAt) return "inactive";
  if (item.status) return String(item.status).toLowerCase();
  return item.active === false ? "inactive" : "active";
}

function getAdminName(item) {
  return (
    String(item.displayName || "").trim() ||
    String(item.name || "").trim() ||
    String(item.fullName || "").trim() ||
    String(item.email || "").trim() ||
    "Unnamed admin"
  );
}

function prettyRole(role) {
  return normalizeAdminRole(role) === "manager" ? "Manager" : "Admin";
}

function formatDate(value) {
  try {
    if (!value) return "—";
    if (value?.seconds) {
      return new Date(value.seconds * 1000).toLocaleString([], {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

export default function AdminsSection() {
  const { profile, user } = useAuth() || {};
  const schoolId = profile?.schoolId || user?.schoolId || "";

  const [staffRows, setStaffRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      if (!schoolId) {
        setStaffRows([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const staffSnap = await getDocs(collection(db, "schools", schoolId, "staff"));

        if (!mounted) return;

        const staffList = staffSnap.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
          }))
          .filter((item) => isAdminLike(item.role));

        setStaffRows(staffList);
      } catch (err) {
        console.error("Failed to load admins:", err);
        setError("Failed to load admin records.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadData();

    return () => {
      mounted = false;
    };
  }, [schoolId]);

  const activeAdmins = useMemo(() => {
    return staffRows.filter((item) => {
      const status = displayStatus(item);
      return !item.deletedAt && status === "active";
    });
  }, [staffRows]);

  const inactiveAdmins = useMemo(() => {
    return staffRows.filter((item) => {
      const status = displayStatus(item);
      return item.deletedAt || status !== "active";
    });
  }, [staffRows]);

  const filteredAdmins = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();

    return staffRows.filter((item) => {
      const status = displayStatus(item);

      if (statusFilter !== "all" && status !== statusFilter) {
        return false;
      }

      if (!q) return true;

      const haystack = [
        getAdminName(item),
        item.email,
        item.role,
        status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [staffRows, search, statusFilter]);

  return (
    <div style={styles.page}>
      <section style={styles.hero}>
        <div>
          <div style={styles.heroEyebrow}>PresentPlus Admin</div>
          <h1 style={styles.heroTitle}>Admin Management</h1>
          <p style={styles.heroText}>
            View school administrators separately from teaching staff so access,
            ownership, and reporting stay clean.
          </p>
        </div>

        <div style={styles.heroMiniCard}>
          <div style={styles.heroMiniLabel}>Active admins</div>
          <div style={styles.heroMiniValue}>{activeAdmins.length}</div>
        </div>
      </section>

      {error ? (
        <div style={{ ...styles.banner, ...styles.bannerError }}>{error}</div>
      ) : null}

      <section style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total admins</div>
          <div style={styles.statValue}>{staffRows.length}</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statLabel}>Active</div>
          <div style={{ ...styles.statValue, color: "#0a7a43" }}>
            {activeAdmins.length}
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statLabel}>Inactive</div>
          <div style={{ ...styles.statValue, color: "#c81e1e" }}>
            {inactiveAdmins.length}
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statLabel}>Managers</div>
          <div style={{ ...styles.statValue, color: "#5b21b6" }}>
            {
              staffRows.filter(
                (item) => normalizeAdminRole(item.role) === "manager"
              ).length
            }
          </div>
        </div>
      </section>

      <section style={styles.card}>
        <div style={styles.cardEyebrow}>Admin directory</div>
        <h2 style={styles.cardTitle}>School admin records</h2>
        <p style={styles.cardText}>
          This is a read-only view for school administrators. New school admins
          are provisioned by super admin only.
        </p>

        <div style={styles.toolbar}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, role, or status"
            style={styles.searchInput}
          />

          <div style={styles.filterWrap}>
            {["all", "active", "inactive"].map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setStatusFilter(item)}
                style={{
                  ...styles.filterBtn,
                  ...(statusFilter === item ? styles.filterBtnActive : {}),
                }}
              >
                {String(item).charAt(0).toUpperCase() + String(item).slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={styles.emptyState}>Loading admin records...</div>
        ) : filteredAdmins.length === 0 ? (
          <div style={styles.emptyState}>No admin records match this view yet.</div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Updated</th>
                </tr>
              </thead>

              <tbody>
                {filteredAdmins.map((row) => {
                  const rowStatus = displayStatus(row);

                  return (
                    <tr key={row.id} style={styles.tr}>
                      <td style={styles.tdStrong}>{getAdminName(row)}</td>
                      <td style={styles.td}>{row.email || "—"}</td>
                      <td style={styles.td}>
                        <span style={styles.rolePill}>
                          {prettyRole(row.role)}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.statusPill,
                            ...(rowStatus === "active"
                              ? styles.statusActive
                              : styles.statusInactive),
                          }}
                        >
                          {String(rowStatus).charAt(0).toUpperCase() +
                            String(rowStatus).slice(1)}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {formatDate(row.updatedAt || row.createdAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

const styles = {
  page: {
    display: "grid",
    gap: 18,
  },

  hero: {
    borderRadius: 30,
    background: "linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)",
    border: "1px solid #dbe7ff",
    padding: 24,
    display: "grid",
    gridTemplateColumns: "1.15fr 0.85fr",
    gap: 16,
    alignItems: "stretch",
  },

  heroEyebrow: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.13em",
    fontWeight: 900,
    color: "#4F46E5",
    marginBottom: 10,
  },

  heroTitle: {
    margin: 0,
    fontSize: 44,
    lineHeight: 0.95,
    letterSpacing: "-0.05em",
    color: "#112042",
    fontWeight: 900,
  },

  heroText: {
    marginTop: 14,
    marginBottom: 0,
    color: "#64748b",
    fontSize: 15,
    lineHeight: 1.7,
    maxWidth: 720,
  },

  heroMiniCard: {
    borderRadius: 24,
    padding: 22,
    color: "#fff",
    background: "linear-gradient(135deg,#4f46e5 0%,#6d5cff 100%)",
    boxShadow: "0 16px 32px rgba(79,70,229,0.16)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },

  heroMiniLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 900,
    color: "rgba(255,255,255,0.80)",
    marginBottom: 10,
  },

  heroMiniValue: {
    fontSize: 42,
    lineHeight: 1,
    fontWeight: 900,
  },

  banner: {
    borderRadius: 18,
    padding: 16,
    fontWeight: 800,
  },

  bannerError: {
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#be123c",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
  },

  statCard: {
    borderRadius: 22,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    padding: 18,
    boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
  },

  statLabel: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 900,
    color: "#64748b",
    marginBottom: 12,
  },

  statValue: {
    fontSize: 46,
    lineHeight: 1,
    fontWeight: 900,
    color: "#112042",
  },

  card: {
    borderRadius: 24,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    padding: 22,
    boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
  },

  cardEyebrow: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 900,
    color: "#64748b",
    marginBottom: 8,
  },

  cardTitle: {
    margin: 0,
    fontSize: 28,
    lineHeight: 1.05,
    fontWeight: 900,
    color: "#112042",
    letterSpacing: "-0.03em",
  },

  cardText: {
    marginTop: 10,
    color: "#64748b",
    lineHeight: 1.7,
    fontSize: 14,
    marginBottom: 18,
  },

  toolbar: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 12,
    alignItems: "center",
    marginBottom: 16,
  },

  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 16,
    border: "1px solid #dbe7ff",
    background: "#ffffff",
    padding: "14px 16px",
    fontSize: 15,
    color: "#112042",
    outline: "none",
  },

  filterWrap: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },

  filterBtn: {
    borderRadius: 999,
    border: "1px solid #dbe7ff",
    background: "#fff",
    color: "#112042",
    fontWeight: 800,
    padding: "10px 14px",
    cursor: "pointer",
  },

  filterBtnActive: {
    background: "#eef2ff",
    color: "#4338ca",
    border: "1px solid #c7d2fe",
  },

  emptyState: {
    borderRadius: 18,
    border: "1px solid #dbe7ff",
    background: "#f8fbff",
    padding: 16,
    color: "#64748b",
    fontWeight: 700,
  },

  tableWrap: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },

  th: {
    textAlign: "left",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#64748b",
    padding: "12px 12px",
    borderBottom: "1px solid #e5eefc",
  },

  tr: {
    background: "#fff",
  },

  td: {
    padding: "14px 12px",
    borderBottom: "1px solid #eef2ff",
    color: "#334155",
    fontSize: 14,
    verticalAlign: "middle",
  },

  tdStrong: {
    padding: "14px 12px",
    borderBottom: "1px solid #eef2ff",
    color: "#112042",
    fontSize: 14,
    fontWeight: 900,
    verticalAlign: "middle",
  },

  rolePill: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "7px 10px",
    background: "#eef2ff",
    color: "#4338ca",
    border: "1px solid #c7d2fe",
    fontSize: 12,
    fontWeight: 900,
  },

  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "7px 10px",
    fontSize: 12,
    fontWeight: 900,
  },

  statusActive: {
    background: "#dcfce7",
    color: "#166534",
    border: "1px solid #bbf7d0",
  },

  statusInactive: {
    background: "#fff1f2",
    color: "#be123c",
    border: "1px solid #fecdd3",
  },
};